/* eslint-disable @typescript-eslint/no-explicit-any */
import { EnvironmentService } from '@core/services/environment.service';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Location } from '@angular/common';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { NotificationDialogComponent } from '@app/shared/components/data-display';
import { MODAL_INTERNAL_ROUTES } from '@app/core/const';
import { NotificationConfig, typePriority } from '@app/shared/types/ui';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  private location = inject(Location);
  private readonly environmentService = inject(EnvironmentService);
  private readonly baseUrl = `${this.environmentService.apiBaseUrl}/User`;
  private notificationList = signal<NotificationConfig[]>([]);
  private fetchingInProgress = false;
  private lastFetchTimestamp = signal<number>(0);
  private readonly cacheTTL = 10 * 60 * 1000;
  private activeRequest: null | ReturnType<typeof this.http.get> = null;
  private isCacheExpired(): boolean {
    const now = Date.now();
    const lastFetch = this.lastFetchTimestamp();
    return (now - lastFetch) > this.cacheTTL;
  }

  fetchNotifications() {
    if (this.notificationList().length > 0 && !this.isCacheExpired()) {
      return of(this.notificationList());
    }

    if (this.fetchingInProgress && this.activeRequest) {
      return this.activeRequest;
    }

    this.fetchingInProgress = true;
    this.activeRequest = this.http.get<NotificationConfig[]>(`${this.baseUrl}/NotificationModals`)
      .pipe(
        map(list => {
          this.notificationList.set(list);
          this.lastFetchTimestamp.set(Date.now());
          this.fetchingInProgress = false;
          this.activeRequest = null;
          return list;
        }),
        catchError((error) => {
          this.fetchingInProgress = false;
          this.activeRequest = null;
          console.error('Error al obtener notificaciones:', error);

          return of([]);
        }),
        shareReplay({ bufferSize: 1, refCount: true, windowTime: 500 })
      );

    return this.activeRequest;
  }

  checkAndShow(userId: string, name: string, companyId: string) {
    if ((this.notificationList().length > 0 && !this.isCacheExpired()) || this.fetchingInProgress) {
      this.processNotifications(userId, name, companyId);
    } else {
      this.fetchNotifications().subscribe({
        next: () => this.processNotifications(userId, name, companyId),
        error: (err) => console.error('Error al cargar notificaciones:', err)
      });
    }
  }

  private processNotifications(userId: string, name: string, companyId: string = '') {
    const currentRoute = this.location.path();
    const modalLoc = MODAL_INTERNAL_ROUTES[currentRoute] ?? currentRoute;

    const notification = this.notificationList()
      .filter(n => n.modalLocation === modalLoc)
      .sort((a, b) => typePriority[a.modalType] - typePriority[b.modalType])
      .shift();

    if (notification) {
      this.dialog.open(NotificationDialogComponent, {
        data: {
          userId,
          name,
          companyId,
          notification
        },
        width: '90%',
        maxWidth: '600px'
      });
    }
  }

  refreshNotifications() {
    if (this.notificationList().length === 0 || this.isCacheExpired()) {
      return this.fetchNotifications();
    }

    return of(this.notificationList());
  }

  forceRefresh() {
    this.lastFetchTimestamp.set(0);
    return this.fetchNotifications();
  }

  resetCache() {
    this.lastFetchTimestamp.set(0);
    this.notificationList.set([]);
  }

  updateState(userModalId: number, opened: number, accepted: boolean): Observable<any> {
    return this.http.post(`${this.baseUrl}/NotificationModalsUpdate`, { userModalId, opened, accepted }).pipe(
      catchError(err => {
        console.error('Error al actualizar estado de notificación:', err);
        return of(null);
      })
    );
  }

}
